/**
 * Shared placement-model helpers.
 *
 * Applications carry a `placements: PlacementEntry[]` array so a single
 * applicant can have MULTIPLE placement records over time (a cancelled
 * placement is never deleted — it stays in the history as `Cancelled`).
 *
 * Status vocabulary (admin-visible, also shown to applicants):
 *   - Placed            the placement is confirmed / active
 *   - Pending Approval  applicant/admin submitted details awaiting approval
 *   - Pending Placement applicant is in the pipeline (no active placement yet)
 *   - Cancelled         the placement was cancelled (kept for history)
 *
 * Designation (`type`) mirrors "Primary Placement" vs "Secondary Job".
 * Only ONE *active* primary placement may exist at a time; a cancelled
 * primary keeps its historical label so the full journey stays readable.
 *
 * This module is intentionally dependency-free so it can be shared by the
 * browser bundle and the Node server bundle.
 */

export const PLACEMENT_STATUSES = [
  'Placed',
  'Pending Approval',
  'Pending Placement',
  'Rejected',
  'Cancelled',
] as const;

export type PlacementStatus = (typeof PLACEMENT_STATUSES)[number];

export type PlacementType = 'primary' | 'secondary';

export type PlacementSource = 'portal-job' | 'manual' | 'self';

export const PLACEMENT_STATUS_SET: ReadonlySet<string> = new Set<string>(PLACEMENT_STATUSES);

export interface PlacementStatusTimelineEntry {
  status: PlacementStatus;
  at: string;
  by?: string;
  note?: string;
}

export interface PlacementEntry {
  placementId: string;
  /** When the placement came from an existing portal Job */
  jobId?: string;
  /** How the placement was entered */
  source: PlacementSource;
  companyName: string;
  jobTitle: string;
  city?: string;
  state?: string;
  country?: string;
  startDate?: string;
  payRate?: string;
  hoursPerWeek?: string;
  /** Supervisor / hiring-contact details (free-form legacy names too) */
  supervisorName?: string;
  supervisorEmail?: string;
  contactName?: string;
  contactEmail?: string;
  notes?: string;
  status: PlacementStatus;
  /** Role designation: 'primary' (Primary Placement) or 'secondary' */
  type: PlacementType;
  /** Optional human note attached to a status transition */
  statusNote?: string;
  placedAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  /** Who entered the record ('employer' = the employer-hire bridge, Phase 2) */
  createdByRole: 'admin' | 'applicant' | 'employer';
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  /** Status-change audit trail (oldest → newest) */
  history?: PlacementStatusTimelineEntry[];
  /** Free-form admin/placement notes */
  placementNotes?: string;
  applicationId?: string;
  /**
   * Housing arrangement for this placement (address, rent, landlord…).
   * Applicants AND team admins may add/edit it while the placement is not yet
   * confirmed; once the placement is Confirmed/Placed (or Cancelled) only team
   * admins can change it. See the placement write endpoints for enforcement.
   */
  housing?: HousingArrangement;
}

/** Accepted rent-charge cadences for a housing arrangement. */
export const HOUSING_RENT_PERIODS = ['Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Yearly'] as const;
export type HousingRentPeriod = (typeof HOUSING_RENT_PERIODS)[number];

export interface HousingArrangement {
  /** Street / unit address of the accommodation */
  address?: string;
  city?: string;
  country?: string;
  zipCode?: string;
  /** Rent amount, kept as the display string the user typed (e.g. "$450") */
  rentCost?: string;
  rentPeriod?: HousingRentPeriod;
  /** Contact person / landlord */
  landlordName?: string;
  landlordPhone?: string;
  /** Any other detail about the accommodation */
  otherDetails?: string;
  updatedAt?: string;
  /** uid of the last editor (admin or the applicant themselves) */
  updatedBy?: string;
  updatedByName?: string;
}

const isNonEmpty = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

/**
 * Normalise arbitrary persisted/form input into a safe HousingArrangement, or
 * `undefined` when the input is empty. Unknown rent cadences are dropped so a
 * stale UI can never persist a value the current vocabulary rejects.
 */
export function normalizeHousingArrangement(value: unknown): HousingArrangement | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw: any = value;
  const cleanStr = (v: unknown, max: number) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
  const rentPeriod = HOUSING_RENT_PERIODS.includes(raw.rentPeriod)
    ? (raw.rentPeriod as HousingRentPeriod)
    : undefined;
  const housing: HousingArrangement = {};
  const address = cleanStr(raw.address, 300);
  const city = cleanStr(raw.city, 120);
  const country = cleanStr(raw.country, 120);
  const zipCode = cleanStr(raw.zipCode, 20);
  const rentCost = cleanStr(raw.rentCost, 80);
  const landlordName = cleanStr(raw.landlordName, 200);
  const landlordPhone = cleanStr(raw.landlordPhone, 40);
  const otherDetails = cleanStr(raw.otherDetails, 3000);
  const updatedAt = typeof raw.updatedAt === 'string' ? raw.updatedAt : undefined;
  const updatedBy = typeof raw.updatedBy === 'string' ? raw.updatedBy : undefined;
  const updatedByName = typeof raw.updatedByName === 'string' ? raw.updatedByName : undefined;

  if (isNonEmpty(address)) housing.address = address;
  if (isNonEmpty(city)) housing.city = city;
  if (isNonEmpty(country)) housing.country = country;
  if (isNonEmpty(zipCode)) housing.zipCode = zipCode;
  if (isNonEmpty(rentCost)) housing.rentCost = rentCost;
  if (rentPeriod) housing.rentPeriod = rentPeriod;
  if (isNonEmpty(landlordName)) housing.landlordName = landlordName;
  if (isNonEmpty(landlordPhone)) housing.landlordPhone = landlordPhone;
  if (isNonEmpty(otherDetails)) housing.otherDetails = otherDetails;
  if (updatedAt) housing.updatedAt = updatedAt;
  if (updatedBy) housing.updatedBy = updatedBy;
  if (updatedByName) housing.updatedByName = updatedByName;

  return Object.keys(housing).some((k) => (housing as any)[k] !== undefined) ? housing : undefined;
}

/**
 * True when the applicant can no longer edit the placement entry — i.e. when a
 * housing/placement change must go through a team admin. A placement entry is
 * locked once it is Confirmed/Placed or Cancelled. The legacy application-level
 * `placementStatus === 'Confirmed'` also locks entries of old single-placement
 * records even when they predate the per-entry array.
 */
export function isPlacementLockedForApplicant(
  entry: Partial<PlacementEntry> | null | undefined,
  legacyPlacementStatus?: string
): boolean {
  if (!entry) return true;
  if (entry.status === 'Placed' || entry.status === 'Cancelled') return true;
  const legacy = String(legacyPlacementStatus || '').toLowerCase();
  if (legacy === 'confirmed' || legacy === 'approved') return true;
  return false;
}

export const isPlacementActive = (p?: Partial<PlacementEntry> | null): boolean =>
  !!p && p.status !== undefined && p.status !== 'Cancelled';

/**
 * Normalise arbitrary persisted data into a safe PlacementEntry array.
 * Tolerates legacy `selfPlacement` snapshots that were written into the
 * array before validation existed.
 */
export function normalizePlacements(value: unknown): PlacementEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((raw: any, i: number): PlacementEntry | null => {
      if (!raw || typeof raw !== 'object') return null;
      const status = PLACEMENT_STATUS_SET.has(raw.status)
        ? (raw.status as PlacementStatus)
        : 'Pending Placement';
      const type = raw.type === 'primary' ? 'primary' : 'secondary';
      const now = new Date().toISOString();
      const source: PlacementSource =
        raw.source === 'portal-job' || raw.source === 'manual' || raw.source === 'self'
          ? raw.source
          : raw.jobId
            ? 'portal-job'
            : 'manual';
      return {
        placementId:
          typeof raw.placementId === 'string' && raw.placementId
            ? raw.placementId
            : `plc_${Date.now().toString(36)}_${i}_${Math.random().toString(36).slice(2, 7)}`,
        jobId: typeof raw.jobId === 'string' ? raw.jobId : undefined,
        source,
        companyName:
          typeof raw.companyName === 'string' ? raw.companyName : raw.company || raw.employerName || '',
        jobTitle:
          typeof raw.jobTitle === 'string'
            ? raw.jobTitle
            : raw.role || raw.position || raw.title || '',
        city: typeof raw.city === 'string' ? raw.city : undefined,
        state: typeof raw.state === 'string' ? raw.state : undefined,
        country: typeof raw.country === 'string' ? raw.country : undefined,
        startDate: typeof raw.startDate === 'string' ? raw.startDate : undefined,
        payRate: typeof raw.payRate === 'string' ? raw.payRate : undefined,
        hoursPerWeek: typeof raw.hoursPerWeek === 'string' ? raw.hoursPerWeek : undefined,
        supervisorName:
          typeof raw.supervisorName === 'string'
            ? raw.supervisorName
            : typeof raw.contactName === 'string'
              ? raw.contactName
              : undefined,
        supervisorEmail:
          typeof raw.supervisorEmail === 'string'
            ? raw.supervisorEmail
            : typeof raw.contactEmail === 'string'
              ? raw.contactEmail
              : undefined,
        contactName: typeof raw.contactName === 'string' ? raw.contactName : undefined,
        contactEmail: typeof raw.contactEmail === 'string' ? raw.contactEmail : undefined,
        notes: typeof raw.notes === 'string' ? raw.notes : undefined,
        status,
        type,
        statusNote: typeof raw.statusNote === 'string' ? raw.statusNote : undefined,
        placedAt: typeof raw.placedAt === 'string' ? raw.placedAt : undefined,
        cancelledAt: typeof raw.cancelledAt === 'string' ? raw.cancelledAt : undefined,
        cancelReason: typeof raw.cancelReason === 'string' ? raw.cancelReason : undefined,
        createdByRole: raw.createdByRole === 'admin' || raw.createdByRole === 'employer' ? raw.createdByRole : 'applicant',
        createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : undefined,
        createdByName: typeof raw.createdByName === 'string' ? raw.createdByName : undefined,
        createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : now,
        updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : now,
        history: Array.isArray(raw.history)
          ? raw.history.filter((h: any) => h && typeof h === 'object')
          : undefined,
        placementNotes:
          typeof raw.placementNotes === 'string' ? raw.placementNotes : undefined,
        applicationId: typeof raw.applicationId === 'string' ? raw.applicationId : undefined,
        housing: normalizeHousingArrangement(raw.housing),
      };
    })
    .filter((p): p is PlacementEntry => (!!p && !!(p.companyName || p.jobTitle || p.jobId)) as boolean);
}

/**
 * Resolve the active primary placement (a placement designated primary that
 * has not been cancelled). At most one can exist at a time; the helper picks
 * the first match so callers never see two.
 */
export function getActivePrimaryPlacement(
  placements: Array<Partial<PlacementEntry>> | PlacementEntry[] | null | undefined
): PlacementEntry | undefined {
  const list = (placements || []) as PlacementEntry[];
  return list.find((p) => p.type === 'primary' && isPlacementActive(p));
}

export function hasActivePrimaryPlacement(
  placements: Array<Partial<PlacementEntry>> | PlacementEntry[] | null | undefined
): boolean {
  return !!getActivePrimaryPlacement(placements);
}

export function getActivePlacements(
  placements: Array<Partial<PlacementEntry>> | PlacementEntry[] | null | undefined
): PlacementEntry[] {
  return ((placements || []) as PlacementEntry[]).filter((p) => isPlacementActive(p));
}

/** Map a legacy single `placementStatus` + `selfPlacement` onto the new vocabulary. */
export function placementStatusFromLegacy(input: {
  placementStatus?: string;
  selfPlacement?: any;
}): PlacementStatus {
  const legacy = input?.placementStatus;
  const self = input?.selfPlacement;
  if (self && (self.status === 'Pending Review' || self.status === 'Approved' || self.status === 'Pending')) {
    return 'Pending Approval';
  }
  if (legacy === 'Confirmed' || legacy === 'Approved') return 'Placed';
  return 'Pending Placement';
}

export interface PlacementSummaryInput {
  placements?: unknown;
  placementStatus?: string;
  selfPlacement?: any;
}

export interface PlacementSummary {
  placements: PlacementEntry[];
  activePrimary?: PlacementEntry;
  activeSecondary: PlacementEntry[];
  cancelled: PlacementEntry[];
  hasPlacements: boolean;
  hasActivePrimary: boolean;
  /** Overall derived placement status used by filters / summaries */
  currentStatus: PlacementStatus;
}

/**
 * Build a summary of an applicant's placement history.
 *
 * currentStatus resolution (in priority order):
 *   1. active primary placement status
 *   2. most recently-updated active (non-cancelled) placement status
 *   3. 'Cancelled' when every recorded placement is cancelled
 *   4. legacy selfPlacement/placementStatus fallback
 *   5. 'Pending Placement' (in pipeline, never placed)
 */
export function getPlacementSummary(input: PlacementSummaryInput): PlacementSummary {
  const placements = normalizePlacements(input?.placements);
  const active = getActivePlacements(placements);
  const activePrimary = active.find((p) => p.type === 'primary');
  const activeSecondary = active.filter((p) => p.type !== 'primary');
  const cancelled = placements.filter((p) => p.status === 'Cancelled');

  let currentStatus: PlacementStatus;
  if (activePrimary) {
    currentStatus = activePrimary.status;
  } else if (active.length > 0) {
    const newest = [...active].sort(
      (a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
    )[0];
    currentStatus = newest.status;
  } else if (cancelled.length > 0) {
    currentStatus = 'Cancelled';
  } else {
    currentStatus = placementStatusFromLegacy(input);
  }

  return {
    placements,
    activePrimary,
    activeSecondary,
    cancelled,
    hasPlacements: placements.length > 0,
    hasActivePrimary: !!activePrimary,
    currentStatus,
  };
}

export function placementStatusLabel(p: PlacementStatus): string {
  return p;
}

/** Sort placements for display: newest first, cancelled entries pushed down. */
export function sortPlacementsForDisplay(
  placements: Array<Partial<PlacementEntry>> | PlacementEntry[]
): PlacementEntry[] {
  const list = (placements || []) as PlacementEntry[];
  const primaryFirst = (p: PlacementEntry) => (p.type === 'primary' && isPlacementActive(p) ? 0 : 1);
  const activeFirst = (p: PlacementEntry) => (isPlacementActive(p) ? 0 : 1);
  const dateDesc = (p: PlacementEntry) => new Date(p.updatedAt || p.createdAt || 0).getTime();
  return [...list].sort((a, b) => {
    const pa = primaryFirst(a) * 10 + activeFirst(a) * 2;
    const pb = primaryFirst(b) * 10 + activeFirst(b) * 2;
    if (pa !== pb) return pa - pb;
    return dateDesc(b) - dateDesc(a);
  });
}

/** Compact one-line label for a placement entry. */
export function placementTitle(p: Partial<PlacementEntry> | null | undefined): string {
  if (!p) return 'Placement';
  const company = p.companyName || p.contactName || 'Unknown employer';
  const role = p.jobTitle || 'Position';
  return `${role} — ${company}`;
}

/** Ring color status type */
export type ProfileRingColor = 'red' | 'blue' | 'green' | 'yellow' | null;

/**
 * Derives the single profile image status ring color based on strict precedence:
 *   RED -> BLUE -> GREEN -> YELLOW
 *
 * Precedence Rules:
 *   1. RED: Application is closed/archived or screening failed.
 *   2. BLUE: Applicant has departed (valid flight/departure details & departed state).
 *   3. GREEN: Applicant is placed/hired (approved placement present).
 *   4. YELLOW: Applicant passed screening and was approved.
 */
export function getProfileStatusRingColor(app: any): ProfileRingColor {
  if (!app) return null;

  const status = String(app.status || app.submissionStatus || '').toLowerCase();
  const masterStage = String(app.masterStage || '').toLowerCase();
  const screeningStatus = String(app.screeningStatus || '').toLowerCase();

  // 1. RED check (Screening Failed / Archived / Closed)
  const isRed =
    status.includes('closed') ||
    status.includes('archived') ||
    status.includes('failed') ||
    screeningStatus.includes('failed') ||
    screeningStatus.includes('rejected') ||
    masterStage.includes('closed') ||
    masterStage.includes('archived');
  if (isRed) return 'red';

  // 2. BLUE check (Departed / Flight Details)
  const flightDetails = app.flightDetails || app.travelDetails || app.itinerary;
  const isDeparted =
    status.includes('departed') ||
    masterStage.includes('departed') ||
    (flightDetails && (flightDetails.departureDate || flightDetails.flightNumber || flightDetails.departed === true));
  if (isDeparted) return 'blue';

  // 3. GREEN check (Placed / Hired)
  const summary = getPlacementSummary({
    placements: app.placements,
    placementStatus: app.placementStatus,
    selfPlacement: app.selfPlacement,
  });
  const isPlaced =
    summary.currentStatus === 'Placed' ||
    status.includes('placed') ||
    status.includes('hired') ||
    masterStage.includes('placed') ||
    masterStage.includes('hired') ||
    app.placementStatus === 'Confirmed';
  if (isPlaced) return 'green';

  // 4. YELLOW check (Screened & Approved)
  const isYellow =
    screeningStatus === 'approved' ||
    screeningStatus === 'passed' ||
    app.screeningApproved === true ||
    status.includes('approved') ||
    status.includes('screened') ||
    masterStage.includes('approved');
  if (isYellow) return 'yellow';

  return null;
}
